import React, { useEffect, useState } from 'react'
import apiClient from '@services/api/client'
import AdminLayout from '../../layouts/AdminLayout'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

const AdminDashboardPage = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPosts: 0,
    totalVideos: 0,
    totalBanners: 0,
    activePosts: 0,
  })
  const [recentActivities, setRecentActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [timePeriod, setTimePeriod] = useState('week') // 'week', 'month', 'year'

  // Données pour le graphique d'activités selon la période
  const activityDataByPeriod = {
    week: [
      { label: 'Lun', publications: 12, vidéos: 8, utilisateurs: 15, bannières: 3 },
      { label: 'Mar', publications: 19, vidéos: 12, utilisateurs: 22, bannières: 5 },
      { label: 'Mer', publications: 15, vidéos: 10, utilisateurs: 18, bannières: 4 },
      { label: 'Jeu', publications: 25, vidéos: 15, utilisateurs: 28, bannières: 6 },
      { label: 'Ven', publications: 22, vidéos: 18, utilisateurs: 32, bannières: 7 },
      { label: 'Sam', publications: 18, vidéos: 14, utilisateurs: 25, bannières: 4 },
      { label: 'Dim', publications: 10, vidéos: 6, utilisateurs: 12, bannières: 2 }
    ],
    month: [
      { label: 'Sem 1', publications: 85, vidéos: 52, utilisateurs: 120, bannières: 18 },
      { label: 'Sem 2', publications: 92, vidéos: 68, utilisateurs: 145, bannières: 22 },
      { label: 'Sem 3', publications: 78, vidéos: 55, utilisateurs: 132, bannières: 19 },
      { label: 'Sem 4', publications: 105, vidéos: 72, utilisateurs: 168, bannières: 28 }
    ],
    year: [
      { label: 'Jan', publications: 280, vidéos: 180, utilisateurs: 450, bannières: 65 },
      { label: 'Fév', publications: 310, vidéos: 195, utilisateurs: 480, bannières: 72 },
      { label: 'Mar', publications: 295, vidéos: 205, utilisateurs: 520, bannières: 68 },
      { label: 'Avr', publications: 340, vidéos: 220, utilisateurs: 565, bannières: 78 },
      { label: 'Mai', publications: 365, vidéos: 240, utilisateurs: 610, bannières: 85 },
      { label: 'Jun', publications: 350, vidéos: 235, utilisateurs: 590, bannières: 82 },
      { label: 'Jul', publications: 380, vidéos: 260, utilisateurs: 640, bannières: 92 },
      { label: 'Aoû', publications: 370, vidéos: 255, utilisateurs: 625, bannières: 88 },
      { label: 'Sep', publications: 390, vidéos: 270, utilisateurs: 670, bannières: 95 },
      { label: 'Oct', publications: 410, vidéos: 285, utilisateurs: 710, bannières: 102 },
      { label: 'Nov', publications: 425, vidéos: 295, utilisateurs: 740, bannières: 108 },
      { label: 'Déc', publications: 320, vidéos: 210, utilisateurs: 580, bannières: 75 }
    ]
  }

  const activityData = activityDataByPeriod[timePeriod]
  
  const periodLabels = {
    week: 'Vue d\'ensemble des 7 derniers jours',
    month: 'Vue d\'ensemble du mois en cours',
    year: 'Vue d\'ensemble de l\'année en cours'
  }

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const [statsRes, activitiesRes] = await Promise.all([
        apiClient.get('/dashboard/stats'),
        apiClient.get('/dashboard/activities'),
      ])
      if (statsRes.success) {
        const d = statsRes.data
        setStats({
          totalUsers:   d.total_clients   || 0,
          totalPosts:   d.total_posts     || 0,
          totalVideos:  d.total_videos    || 0,
          totalBanners: d.total_banners   || 0,
          activePosts:  d.active_posts    || 0,
        })
      }
      if (activitiesRes.success) {
        setRecentActivities(activitiesRes.data || [])
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400 text-lg">Chargement...</div>
        </div>
      </AdminLayout>
    )
  }

  console.log('[AdminDashboardPage] Rendering - Loading:', loading, 'Stats:', stats)

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}>
        {/* Stats Cards avec design soft */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Utilisateurs */}
          <div className="bg-white rounded-md border border-slate-200 p-6 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Utilisateurs</p>
                <p className="text-3xl font-semibold text-gray-800 mt-2">{stats.totalUsers}</p>
                <p className="text-green-500 text-xs mt-2 font-medium">+12% ce mois</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Publications */}
          <div className="bg-white rounded-md border border-slate-200 p-6 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Publications</p>
                <p className="text-3xl font-semibold text-gray-800 mt-2">{stats.totalPosts}</p>
                <p className="text-gray-400 text-xs mt-2">{stats.activePosts} actives</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Vidéos */}
          <div className="bg-white rounded-md border border-slate-200 p-6 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Vidéos</p>
                <p className="text-3xl font-semibold text-gray-800 mt-2">{stats.totalVideos}</p>
                <p className="text-gray-400 text-xs mt-2">Contenu vidéo</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Bannières */}
          <div className="bg-white rounded-md border border-slate-200 p-6 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Bannières</p>
                <p className="text-3xl font-semibold text-gray-800 mt-2">{stats.totalBanners}</p>
                <p className="text-gray-400 text-xs mt-2">Publicités</p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Graphique des activités de la plateforme */}
        <div className="bg-white rounded-md border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Activités de la plateforme</h2>
              <p className="text-sm text-gray-500 mt-1">{periodLabels[timePeriod]}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setTimePeriod('week')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  timePeriod === 'week'
                    ? 'bg-[#002A4E] text-white '
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Semaine
              </button>
              <button
                onClick={() => setTimePeriod('month')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  timePeriod === 'month'
                    ? 'bg-[#002A4E] text-white '
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Mois
              </button>
              <button
                onClick={() => setTimePeriod('year')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  timePeriod === 'year'
                    ? 'bg-[#002A4E] text-white '
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Année
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={activityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="label" 
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Line 
                type="monotone" 
                dataKey="publications" 
                stroke="#10B981" 
                strokeWidth={2}
                dot={{ fill: '#10B981', r: 4 }}
                activeDot={{ r: 6 }}
                name="Publications"
              />
              <Line 
                type="monotone" 
                dataKey="vidéos" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                dot={{ fill: '#8B5CF6', r: 4 }}
                activeDot={{ r: 6 }}
                name="Vidéos"
              />
              <Line 
                type="monotone" 
                dataKey="utilisateurs" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', r: 4 }}
                activeDot={{ r: 6 }}
                name="Utilisateurs"
              />
              <Line 
                type="monotone" 
                dataKey="bannières" 
                stroke="#F59E0B" 
                strokeWidth={2}
                dot={{ fill: '#F59E0B', r: 4 }}
                activeDot={{ r: 6 }}
                name="Bannières"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Graphique de distribution des rôles */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Activités récentes */}
          <div className="bg-white rounded-md border border-slate-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">Activités récentes</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {recentActivities.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 transition-colors">
                    <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-medium text-sm">
                      {activity.userName?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 text-sm">{activity.userName}</p>
                      <p className="text-xs text-gray-500">{activity.action}</p>
                    </div>
                    <div className="text-xs text-gray-400">{activity.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Statistiques détaillées */}
          <div className="bg-white rounded-md border border-slate-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">Vue d'ensemble</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white border border-slate-200 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Posts actifs</p>
                      <p className="text-xs text-gray-500">En ligne actuellement</p>
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-gray-800">{stats.activePosts}</p>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white border border-slate-200 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Vues totales</p>
                      <p className="text-xs text-gray-500">Toutes publications</p>
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-gray-400">-</p>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white border border-slate-200 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Commentaires</p>
                      <p className="text-xs text-gray-500">Total interactions</p>
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-gray-400">-</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminDashboardPage
